<template>
        <BaseDropdown v-bind="$props"
                      :customDisplayText="dropdownFieldTitle"
                      :height="height"
                      :maxListHeight="maxListHeight"
                      :triggerCloseMenu="triggerClose"
                      @closeMenu="closeMenu"
                      @openMenu="openMenu" v-on="$listeners">
            <template #dropdownField="slotProps">
                <slot name="dropdownField" :dropdownFieldTitle="slotProps.dropdownFieldTitle"/>
            </template>
            <template #dropdownArea>
                <div>
                    <div class="actions-wrap" v-if="searchShown">
                        <input type="search"
                               :ref="'search' + uniqueId"
                               class="dropdown-search font-5"
                               placeholder="Search"
                               v-model="searchText"
                               v-if="searchShown"
                               v-once
                               @input="searchOptions(searchText)"
                               :id="`searchDropdown${uniqueId}`"
                        >
                        <slot name="actionsWrap"></slot>
                    </div>
                    <div v-if="noSearchResults"
                         class="font-4 search-no-results">
                        {{ noResultsIndication }}
                    </div>
                    <MultipleSelectionLeavesDropdown
                        v-if="!noSearchResults"
                        :style="{'max-height': getMaxListHeight}"
                        :options="filteredOptions"
                        :selectedItems="selectedItems"
                        :idField="idField"
                        :displayField="displayField"
                        :showLeavesBorder="showLeavesBorder"
                        :small="small"
                        @dropdownSelectedAll="onSelectAll"
                        @dropdownSelectedOption="onClick">
                        <template #postOption="slotProps">
                            <slot name="postOption" :option="slotProps.option"/>
                        </template>
                        <template #preOption="slotProps">
                            <slot name="preOption" :option="slotProps.option"/>
                        </template>
                        <template #description="slotProps">
                            <slot name="description" :option="slotProps.option"/>
                        </template>
                    </MultipleSelectionLeavesDropdown>
                </div>
            </template>
            <template #dropdownFooter>
                <div class="footer">
                    <button type="button" class="secondary small" @click="closeMenu">Cancel</button>
                    <button type="submit" class="primary small" :id="`submitButtonDropdown${uniqueId}`"
                            @click="applySelection">Apply</button>
                </div>
            </template>
        </BaseDropdown>
</template>

<script>
import BaseDropdown from 'components/dropdowns/BaseDropdown.vue';
import MultipleSelectionLeavesDropdown from './MultipleSelectionLeavesDropdown.vue';

export default {
    name: 'MultiSelectBaseDropdown',
    components: {
        BaseDropdown,
        MultipleSelectionLeavesDropdown
    },
    data() {
        return {
            selectedItems: this.searchSelectedIds(),
            searchText: '',
            filteredOptions: this.options,
            triggerClose: false
        };
    },
    props: {
        ...BaseDropdown.props,
        selectedIds: {
            type: Array,
            default: () => []
        },
        searchFields: {
            type: Array,
            default: () => []
        },
        selectionItemDescriptor: {
            type: String,
            default: ''
        },
        searchEnabled: {
            type: Boolean,
            default: true
        },
        givenSearchedText: String,
        showSelectAll: Boolean,
        firstSelectedAsCount: Boolean,
        small: Boolean,
        noResultsIndication: {
            type: String,
            default: 'No Results'
        },
        showLeavesBorder: {
            type: Boolean,
            default: true
        }
    },
    computed: {
        dropdownFieldTitle() {
            if (this.selectedItems.length === 0) {
                return this.defaultItem[this.selectedDisplayTextField];
            }
            if (this.selectedItems.length === 1 && !this.firstSelectedAsCount) {
                const item = this.selectedItems[0];
                if (item) {
                    return item[this.selectedDisplayTextField];
                }
            }
            return `${this.selectedItems.length}
                ${(this.selectionItemDescriptor ? `${this.selectionItemDescriptor} ` : ' ')}Selected`;
        },
        defaultItem() {
            return { [this.idField]: '', [this.selectedDisplayTextField]: this.defaultText || '' };
        },
        searchShown() {
            return this.options.length > 8 && this.searchEnabled;
        },
        getMaxListHeight() {
            return `${this.maxListHeight}px`;
        },
        noSearchResults() {
            const minOptionsLength = this.showSelectAll ? 1 : 0;
            return (this.givenSearchedText || this.searchShown)
                && this.filteredOptions.length === minOptionsLength;
        }
    },
    watch: {
        selectedIds() {
            this.selectedItems = this.searchSelectedIds();
        },
        options() {
            this.searchOptions(this.searchText);
            this.selectedItems = this.searchSelectedIds();
        },
        givenSearchedText() {
            this.searchOptions(this.givenSearchedText);
        }
    },
    methods: {
        closeMenu() {
            this.searchText = '';
            this.filteredOptions = this.options;
            this.selectedItems = this.searchSelectedIds();
            this.triggerClose = true;
        },
        openMenu() {
            this.triggerClose = false;
            this.$nextTick().then(() => {
                if (this.searchShown) {
                    this.$refs[`search${this.uniqueId}`].focus();
                }
            });
        },
        onSelectAll({ isCheck }) {
            if (isCheck) {
                this.selectedItems = this.selectedItems.concat(
                    [...this.filteredOptions.filter(
                        (option) => !option.isSelectAll && !this.selectedItems.some((item) => item.id === option.id)
                    )]
                );
            } else {
                const selectedFiltered = this.selectedItems.filter((option) => this.filteredOptions.includes(option));
                this.selectedItems = this.selectedItems.filter((option) => selectedFiltered.indexOf(option) < 0);
            }
            this.$emit('selectAllClicked', this.selectedItems.map((item) => item[this.idField]));
        },
        onClick({ option, isCheck }) {
            if (isCheck) {
                this.selectedItems.push(option);
                this.$emit('selectedItem', this.selectedItems.map((item) => item[this.idField]));
            } else {
                this.selectedItems.splice(this.selectedItems.indexOf(option), 1);
                this.$emit('unselectedItem', this.selectedItems.map((item) => item[this.idField]));
            }
        },
        applySelection() {
            this.$emit('dropdownSelectedOptions', {
                selected: this.selectedItems.map((item) => ({ ...item }))
            });
            this.closeMenu();
        },
        searchSelectedIds() {
            return this.options.filter((x) => this.selectedIds.includes(x[this.idField]));
        },
        searchOptions(value) {
            if (value) {
                const searchValue = value.toLowerCase();
                const searchFields = this.searchFields.length ? this.searchFields : [this.displayField];
                this.filteredOptions = this.options.filter(
                    (option) => option.isHeader || option.isSelectAll
                        || searchFields.some((field) => option[field].toLowerCase().indexOf(searchValue) > -1)
                );
            } else {
                this.filteredOptions = this.options;
            }
        }
    }
};
</script>

<style scoped lang="scss">
@import '~styles/styleGuide';
@import '~styles/buttons';

$smallDropdownHeight: 25px;
$mediumDropdownHeight: 32px;
$largeDropdownHeight: 35px;

$dropdownHeightField: $mediumDropdownHeight;
$dropdownBorderBTWElements: $border;

$dropdownItemHeight: 40px;
$dropdownBorderBTWElements: $border;
$paddingLeftElements: 18px;


.dropdown-search {
    background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20viewBox%3D%220%200%2038%2038%22%3E%0A%20%20%20%20%3Cpath%20fill%3D%22%2395a6b8%22%20fill-rule%3D%22nonzero%22%20d%3D%22M15.155%200C6.777%200%200%206.621%200%2014.808s6.777%2014.808%2015.155%2014.808c2.992%200%205.76-.857%208.107-2.314L34.212%2038%2038%2034.298%2027.19%2023.764c1.944-2.491%203.12-5.577%203.12-8.956C30.31%206.621%2023.535%200%2015.156%200zm0%203.484c6.418%200%2011.59%205.053%2011.59%2011.324s-5.172%2011.324-11.59%2011.324S3.566%2021.079%203.566%2014.808c0-6.27%205.171-11.324%2011.59-11.324z%22/%3E%0A%3C/svg%3E");
    background-size: 15px 15px;
    background-repeat: no-repeat;
    background-position: center right 10px;
    padding-right: 25px;
    -webkit-appearance: textfield; // safari input-type:search fix
    margin: 0;
    width: 100%;
    min-width: 140px;
    border-radius: $borderRadius;
    line-height: 35px;
    height: 35px;
    border: $border;
    padding-left: 10px;
    background-color: $white;
    outline: none;

    /* clears the 'X' from Internet Explorer */
    &::-ms-clear {
        display: none;
        width : 0;
        height: 0;
    }
    &::-ms-reveal {
        display: none;
        width : 0;
        height: 0;
    }

    /* clears the 'X' from Chrome */
    &::-webkit-search-decoration,
    &::-webkit-search-cancel-button,
    &::-webkit-search-results-button,
    &::-webkit-search-results-decoration {
        display: none;
    }


    &:focus {
        border: solid 1px $primaryColor;
    }

    &[disabled] {
        background-color: $light-200;
        color: $light-800;
    }

    &::-webkit-input-placeholder, &:-moz-placeholder, &::-moz-placeholder, &:-ms-input-placeholder {
        color: $light-800;
    }

    &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 1000px $white inset;
    }
}

.actions-wrap {
    padding: 8px;
    background-color: $white;
    box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.1);
    margin-bottom: 2px;

    &.padding {
        padding-bottom: 20px;
    }
}

.search-no-results {
    color: $light-800;
    padding: 13px 20px 8px 20px;
    white-space: nowrap;
    background-color: $white;
}

.footer {
    height: 37px;
    padding-bottom: 35px;
    display: grid;
    grid-template-columns: 80px 80px;
    justify-content: end;
    padding-right: 12px;
    padding-top: 13px;
    border-top: $border;
    button {
        margin-left: 10px;
    }
}

</style>
